import { API_ORIGIN, apiUrl } from "./apiOrigin";
// StaffAbsences.js — absences across the fleet
// =============================================================================
// Opened from Head Office → Staff → Staff absences.
//
// A grid of pharmacies, one column per absence type. A radio selector switches
// what the cells hold: events, days, or hours. Clicking a row expands it to
// show the individual absences making up that row — staff name, full dates,
// and the type again.
//
// Defaults to the current year from 1 January; any date range can be chosen.
//
// Backend:
//   POST /api/holidayRota/branches            (existing — the branch list)
//   POST /api/staffAbsences/summary           (new — see staff_absences.js)
//     { from, to } -> { rows:[...], types:[...], grandTotal:{...} }
//   POST /api/staffAbsences/detail            (new)
//     { dbName, pharmacyId, from, to } -> { absences:[...] }
// =============================================================================

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { displayName, useMaskNames, MaskToggleButton } from "./nameMask";
const API = apiUrl("");

// Same palette the holiday rota uses, so a type reads the same colour in both
// screens. Unknown types fall back to OTHER rather than being dropped.
const TYPE_COLOURS = {
  SICK_LEAVE: "#CF1322",
  UNAUTHORISED: "#b45309",
  EMERGENCY: "#ea580c",
  OTHER: "#5D5D5D"
};
const pretty = s => (s || "").toString().replace(/_/g, " ").toLowerCase().replace(/\b\w/g, c => c.toUpperCase());
const iso = d => {
  const x = new Date(d);
  return `${x.getFullYear()}-${String(x.getMonth() + 1).padStart(2, "0")}-${String(x.getDate()).padStart(2, "0")}`;
};
const fmtDate = d => d ? new Date(d).toLocaleDateString("en-GB", {
  day: "numeric",
  month: "short",
  year: "numeric"
}) : "—";

/** Events/days are whole numbers; hours can be fractional. */
const fmtValue = (n, measure) => {
  if (!n) return "";
  return measure === "hours" ? (Math.round(n * 10) / 10).toLocaleString() : Math.round(n).toLocaleString();
};
export default function StaffAbsences() {
  const token = localStorage.getItem("authToken");
  // useMaskNames() returns a plain boolean; the toggle button manages the
  // flag itself and renders nothing for non-admin viewers.
  const maskNames = useMaskNames();

  // 1 January of the current year to today — the usual "so far this year"
  // question. Both ends are editable.
  const yearStart = useMemo(() => iso(new Date(new Date().getFullYear(), 0, 1)), []);
  const today = useMemo(() => iso(new Date()), []);
  const [from, setFrom] = useState(yearStart);
  const [to, setTo] = useState(today);
  const [measure, setMeasure] = useState("events"); // events | days | hours
  const [rows, setRows] = useState([]);
  const [types, setTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [openId, setOpenId] = useState(null);
  const [detail, setDetail] = useState({}); // pharmacyId -> absences[]
  const [detailBusy, setDetailBusy] = useState(null);
  const [sort, setSort] = useState({
    by: "name",
    dir: 1
  });
  const post = useCallback(async (path, body) => {
    const res = await fetch(`${API}${path}`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`
      },
      body: JSON.stringify(body)
    });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return res.json();
  }, [token]);
  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    setOpenId(null);
    setDetail({});
    try {
      const d = await post("/api/staffAbsences/summary", {
        from,
        to
      });
      setRows(Array.isArray(d?.rows) ? d.rows : []);
      // Column order comes from the server so every branch shares one set of
      // columns, including types that only one branch has ever used.
      setTypes(Array.isArray(d?.types) ? d.types : []);
    } catch (e) {
      setError(String(e.message || e));
    } finally {
      setLoading(false);
    }
  }, [post, from, to]);
  useEffect(() => {
    load();
  }, [load]);
  const toggle = async r => {
    if (openId === r.pharmacyId) {
      setOpenId(null);
      return;
    }
    setOpenId(r.pharmacyId);
    if (detail[r.pharmacyId]) return;
    setDetailBusy(r.pharmacyId);
    try {
      const d = await post("/api/staffAbsences/detail", {
        dbName: r.dbName,
        pharmacyId: r.pharmacyId,
        from,
        to
      });
      setDetail(m => ({
        ...m,
        [r.pharmacyId]: d.absences || []
      }));
    } catch (e) {
      setDetail(m => ({
        ...m,
        [r.pharmacyId]: {
          error: String(e.message || e)
        }
      }));
    } finally {
      setDetailBusy(null);
    }
  };
  const valueFor = (row, type) => row.totals?.[type]?.[measure] || 0;
  const rowTotal = row => types.reduce((a, t) => a + valueFor(row, t), 0);
  const sorted = useMemo(() => {
    const list = [...rows];
    list.sort((a, b) => {
      let d;
      if (sort.by === "name") d = String(a.pharmacyName).localeCompare(String(b.pharmacyName));else if (sort.by === "total") d = rowTotal(a) - rowTotal(b);else d = valueFor(a, sort.by) - valueFor(b, sort.by);
      return d * sort.dir;
    });
    return list;
  }, [rows, sort, measure, types]); // eslint-disable-line react-hooks/exhaustive-deps

  const columnTotal = type => rows.reduce((a, r) => a + valueFor(r, type), 0);
  const grandTotal = rows.reduce((a, r) => a + rowTotal(r), 0);
  const head = (label, key, align = "right") => <th onClick={() => setSort(s => ({
    by: key,
    dir: s.by === key ? -s.dir : 1
  }))} style={{
    ...S.th,
    textAlign: align,
    cursor: "pointer"
  }} title="Click to sort">
      {label}{sort.by === key ? sort.dir === 1 ? " ▲" : " ▼" : ""}
    </th>;
  return <div style={{
    padding: 16,
    fontFamily: "system-ui, sans-serif",
    color: "#262626"
  }}>
      <div style={{
      display: "flex",
      alignItems: "baseline",
      gap: 12,
      marginBottom: 4
    }}>
        <h2 style={{
        margin: 0,
        fontSize: 20,
        fontWeight: 600
      }}>Staff absences</h2>
        <MaskToggleButton />
      </div>
      <p style={{
      margin: "0 0 16px",
      color: "#5D5D5D",
      fontSize: 13
    }}>
        Recorded absences by branch and type. Click any branch to see the
        individual absences behind its figures.
      </p>

      {/* ---- controls ---- */}
      <div style={{
      display: "flex",
      gap: 18,
      flexWrap: "wrap",
      alignItems: "flex-end",
      marginBottom: 16,
      padding: "12px 14px",
      background: "#FAFAFA",
      border: "1px solid #F0F0F0",
      borderRadius: 8
    }}>
        <label style={S.ctl}>
          <span style={S.ctlLabel}>From</span>
          <input type="date" value={from} max={to} onChange={e => setFrom(e.target.value)} style={S.input} />
        </label>
        <label style={S.ctl}>
          <span style={S.ctlLabel}>To</span>
          <input type="date" value={to} min={from} onChange={e => setTo(e.target.value)} style={S.input} />
        </label>

        <div style={S.ctl}>
          <span style={S.ctlLabel}>Show</span>
          <div style={{
          display: "flex",
          gap: 14,
          paddingBottom: 4
        }}>
            {[["events", "Events"], ["days", "Days"], ["hours", "Hours"]].map(([v, l]) => <label key={v} style={{
            display: "flex",
            alignItems: "center",
            gap: 5,
            fontSize: 13,
            cursor: "pointer"
          }}>
                <input type="radio" name="measure" value={v} checked={measure === v} onChange={() => setMeasure(v)} />
                {l}
              </label>)}
          </div>
        </div>

        {/* Quick ranges — the three people actually ask for. */}
        <div style={{
        display: "flex",
        gap: 6,
        paddingBottom: 4
      }}>
          <button style={S.chip} onClick={() => {
          setFrom(yearStart);
          setTo(today);
        }}>
            This year
          </button>
          <button style={S.chip} onClick={() => {
          const y = new Date().getFullYear() - 1;
          setFrom(iso(new Date(y, 0, 1)));
          setTo(iso(new Date(y, 11, 31)));
        }}>Last year</button>
          <button style={S.chip} onClick={() => {
          const d = new Date();
          d.setMonth(d.getMonth() - 3);
          setFrom(iso(d));
          setTo(today);
        }}>Last 3 months</button>
        </div>
      </div>

      {measure !== "events" &&
    // Said plainly. Both figures come from the working pattern, not from
    // the absence record, so a person with no pattern recorded shows zero
    // — which is a gap in the rota, not a person who was never off.
    <div style={S.note}>
          {measure === "days" ? "Days counts scheduled working days only \u2014 a Friday-to-Monday absence for someone who works Mon/Wed/Fri counts as 2 days. Expand a branch to see every date recorded." : "Hours are the contracted hours of those scheduled days, less any unpaid break. Days with no working pattern recorded count as zero."}
        </div>}

      {loading && <div style={{
      color: "#5D5D5D"
    }}>Loading…</div>}
      {error && <div style={S.err}>Could not load absences: {error}</div>}

      {!loading && !error && rows.length === 0 && <div style={{
      color: "#5D5D5D"
    }}>No absences recorded in this range.</div>}

      {!loading && !error && rows.length > 0 && <div style={{
      overflowX: "auto",
      border: "1px solid #F0F0F0",
      borderRadius: 8
    }}>
          <table style={{
        borderCollapse: "collapse",
        width: "100%",
        fontSize: 13
      }}>
            <thead>
              <tr style={{
            background: "#F5F5F5"
          }}>
                {head("Pharmacy", "name", "left")}
                {types.map(t => <th key={t} onClick={() => setSort(s => ({
              by: t,
              dir: s.by === t ? -s.dir : 1
            }))} style={{
              ...S.th,
              textAlign: "right",
              cursor: "pointer"
            }} title="Click to sort">
                    <span style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 6
              }}>
                      <span style={{
                  width: 8,
                  height: 8,
                  borderRadius: 2,
                  background: TYPE_COLOURS[t] || TYPE_COLOURS.OTHER,
                  display: "inline-block"
                }} />
                      {pretty(t)}
                    </span>
                    {sort.by === t ? sort.dir === 1 ? " ▲" : " ▼" : ""}
                  </th>)}
                {head("Total", "total")}
              </tr>
            </thead>
            <tbody>
              {sorted.map(r => {
            const open = openId === r.pharmacyId;
            return <React.Fragment key={r.pharmacyId}>
                    <tr onClick={() => toggle(r)} style={{
                cursor: "pointer",
                background: open ? "#E6F7FF" : "#fff",
                borderTop: "1px solid #F0F0F0"
              }}>
                      <td style={{
                  ...S.td,
                  fontWeight: 500
                }}>
                        <span style={{
                    color: "#8C8C8C",
                    marginRight: 6
                  }}>
                          {open ? "▾" : "▸"}
                        </span>
                        {displayName(r.pharmacyName, maskNames)}
                      </td>
                      {types.map(t => <td key={t} style={{
                  ...S.td,
                  textAlign: "right"
                }}>
                          {fmtValue(valueFor(r, t), measure) || <span style={{
                    color: "#D9D9D9"
                  }}>–</span>}
                        </td>)}
                      <td style={{
                  ...S.td,
                  textAlign: "right",
                  fontWeight: 600
                }}>
                        {fmtValue(rowTotal(r), measure) || "–"}
                      </td>
                    </tr>

                    {open && <tr>
                        <td colSpan={types.length + 2} style={{
                  padding: 0,
                  background: "#fafafa"
                }}>
                          <BranchDetail rows={detail[r.pharmacyId]} busy={detailBusy === r.pharmacyId} maskNames={maskNames} />
                        </td>
                      </tr>}
                  </React.Fragment>;
          })}
            </tbody>
            <tfoot>
              <tr style={{
            background: "#F5F5F5",
            borderTop: "2px solid #D9D9D9"
          }}>
                <td style={{
              ...S.td,
              fontWeight: 600
            }}>All branches</td>
                {types.map(t => <td key={t} style={{
              ...S.td,
              textAlign: "right",
              fontWeight: 600
            }}>
                    {fmtValue(columnTotal(t), measure) || "–"}
                  </td>)}
                <td style={{
              ...S.td,
              textAlign: "right",
              fontWeight: 700
            }}>
                  {fmtValue(grandTotal, measure) || "–"}
                </td>
              </tr>
            </tfoot>
          </table>
        </div>}
    </div>;
}

/* -------------------------------------------------------------------------- */

function BranchDetail({
  rows,
  busy,
  maskNames
}) {
  if (busy) return <div style={{
    padding: 14,
    color: "#5D5D5D"
  }}>Loading absences…</div>;
  if (!rows) return null;
  if (rows.error) return <div style={{
    padding: 14,
    color: "#CF1322"
  }}>Could not load: {rows.error}</div>;
  if (!rows.length) return <div style={{
    padding: 14,
    color: "#5D5D5D"
  }}>No absences in this range.</div>;
  return <div style={{
    padding: "10px 14px 14px"
  }}>
      <table style={{
      borderCollapse: "collapse",
      width: "100%",
      fontSize: 12.5
    }}>
        <thead>
          <tr style={{
          color: "#5D5D5D",
          textAlign: "left"
        }}>
            <th style={S.thin}>Staff</th>
            <th style={S.thin}>Type</th>
            <th style={S.thin}>From</th>
            <th style={S.thin}>To</th>
            <th style={{
            ...S.thin,
            textAlign: "right"
          }}>Working days</th>
            <th style={{
            ...S.thin,
            textAlign: "right"
          }}>Dates</th>
            <th style={{
            ...S.thin,
            textAlign: "right"
          }}>Hours</th>
            <th style={S.thin}>Notes</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((a, i) => <tr key={a.id || i} style={{
          borderTop: "1px solid #eee",
          background: "#fff"
        }}>
              <td style={S.tdin}>{displayName(a.staffName, maskNames)}</td>
              <td style={S.tdin}>
                <span style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 6
            }}>
                  <span style={{
                width: 8,
                height: 8,
                borderRadius: 2,
                display: "inline-block",
                background: TYPE_COLOURS[a.absenceType] || TYPE_COLOURS.OTHER
              }} />
                  {pretty(a.absenceType)}
                </span>
              </td>
              <td style={S.tdin}>{fmtDate(a.startDate)}</td>
              <td style={S.tdin}>{fmtDate(a.endDate)}</td>
              <td style={{
            ...S.tdin,
            textAlign: "right"
          }}>{a.days ?? "—"}</td>
              {/* Every date on the record, so a spell that spans days the
                  person does not work is explainable rather than puzzling. */}
              <td style={{
            ...S.tdin,
            textAlign: "right",
            color: a.recordedDays !== a.days ? "#b45309" : "#8C8C8C"
          }} title={a.recordedDays !== a.days ? "Some of these dates are not scheduled working days" : ""}>
                {a.recordedDays ?? "—"}
              </td>
              <td style={{
            ...S.tdin,
            textAlign: "right"
          }}>
                {a.hours != null ? Math.round(a.hours * 10) / 10 : "—"}
              </td>
              <td style={{
            ...S.tdin,
            color: "#5D5D5D"
          }}>{a.notes || ""}</td>
            </tr>)}
        </tbody>
      </table>
    </div>;
}
const S = {
  th: {
    padding: "10px 12px",
    fontWeight: 600,
    fontSize: 12,
    color: "#434343",
    borderBottom: "1px solid #F0F0F0",
    whiteSpace: "nowrap"
  },
  td: {
    padding: "9px 12px",
    whiteSpace: "nowrap"
  },
  thin: {
    padding: "6px 10px",
    fontWeight: 600,
    fontSize: 11.5,
    whiteSpace: "nowrap"
  },
  tdin: {
    padding: "7px 10px",
    whiteSpace: "nowrap"
  },
  ctl: {
    display: "flex",
    flexDirection: "column",
    gap: 4
  },
  ctlLabel: {
    fontSize: 11,
    color: "#5D5D5D",
    fontWeight: 600,
    textTransform: "uppercase",
    letterSpacing: ".04em"
  },
  // index.css also sets input { font-size: 16px; padding: 8px; height: 36px }
  // globally. Inline wins, but the height must be restated or the date fields
  // sit taller than the controls beside them.
  input: {
    padding: "6px 8px",
    border: "1px solid #D9D9D9",
    borderRadius: 6,
    fontSize: 13,
    height: 32,
    boxSizing: "border-box",
    color: "#262626",
    background: "#fff",
    fontFamily: "inherit"
  },
  // `color` is NOT optional here. index.css carries a global
  //   button { background-color: #0E8CFF; color: white; }
  // Inline styles beat it for background, so setting only `background` gave a
  // white pill with white text — an invisible label on a visible button.
  // Anything rendered as a <button> in this file must set its own colour.
  chip: {
    padding: "6px 12px",
    border: "1px solid #D9D9D9",
    background: "#fff",
    color: "#262626",
    borderRadius: 999,
    fontSize: 12.5,
    lineHeight: 1.2,
    cursor: "pointer",
    fontFamily: "inherit"
  },
  note: {
    marginBottom: 12,
    padding: "8px 12px",
    background: "#FFFBE6",
    border: "1px solid #FFE58F",
    borderRadius: 6,
    fontSize: 12.5,
    color: "#AD4E00"
  },
  err: {
    padding: 12,
    background: "#FFF1F0",
    border: "1px solid #FFCCC7",
    borderRadius: 6,
    color: "#CF1322",
    fontSize: 13
  }
};
